import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
// import { useSpring, animated } from "@react-spring/web";
import { LiaSearchSolid } from "react-icons/lia";
import { IoMdClose } from "react-icons/io";
import { useDebounce } from "@/client/hooks";
import { useRouter } from "next/navigation";
import Link from "next/link";

import "./Modal.css";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
};

export type CourseProps = {
  id: number;
  slug: string;
  category: string;
  title: string;
  thumbnail: string;
  description: string;
  excerpt: string;
};

export type ArticleProps = {
  id: string;
  title: string;
  slug: string;
  category: string;
  thumbnail: string;
  day: string;
  month: string;
  year: string;
};

export type QuickLinksProps = {
  id: string;
  title: string;
  url: string;
  excerpt: string;
};
const Modal: React.FC<ModalProps> = ({ isOpen, onClose, setOpenModal }) => {
  if (!isOpen) return null;
  return (
    <SearchDialog onClose={onClose} setOpenModal={setOpenModal} />
  );
};

const SearchDialog: React.FC<{
  onClose: () => void;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
}> = ({ onClose, setOpenModal }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus management: remember what was focused, trap focus while open,
  // and restore focus to the trigger on close. [SC 2.4.3], [SC 2.1.2]
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus into the dialog (the search input has autoFocus, but ensure it).
    const input = dialogRef.current?.querySelector<HTMLInputElement>(
      'input[type="search"]'
    );
    input?.focus();

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      FOCUSABLE_SELECTOR
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    // The backdrop closes on click as a pointer convenience; keyboard users have
    // the Escape key and the visible Close button below.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="react-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Dialog container handles Escape + focus-trap key events. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
        className="react-modal-content mt-28"
        onKeyDown={handleKeyDown}
      >
        <h2 id="search-modal-title" className="sr-only">
          Search the site
        </h2>
        <div className="modal-header">
          <button
            type="button"
            className="btn-close"
            aria-label="Close search"
            onClick={onClose}
          >
            <IoMdClose className="close-icon" aria-hidden="true" />
          </button>
        </div>
        <ModalBody setOpenModal={setOpenModal} />
      </div>
    </div>
  );
};

type BodyProps = {
  setOpenModal: Dispatch<SetStateAction<boolean>>;
};
const ModalBody: React.FC<BodyProps> = ({ setOpenModal }) => {
  const [searchText, setSearchText] = useState("");
  const [courses, setCourses] = useState<CourseProps[]>([]);
  const [articles, setArticles] = useState<ArticleProps[]>([]);
  const [quicklinks, setQuickLinks] = useState<QuickLinksProps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const debounce = useDebounce(searchText, 500);
  const router = useRouter();

  const searchQuery: () => void = useCallback(() => {
    if (!searchText || searchText.trim().length === 0) return;
    setOpenModal(false);
    try {
      router.push(`/results?query=${searchText}`);
    } catch (error: any) {
      console.error("Error navigating to results page", error);
    }
  }, [router, searchText, setOpenModal]);

  useEffect(() => {
    const filterSearch = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const [courses_response, articles_response, quicklinks_response] =
          await Promise.all([
            fetch(`/api/courses?query=${searchText}`, { method: "GET" }),
            fetch(`/api/articles?query=${searchText}`, { method: "GET" }),
            fetch(`/api/quicklinks?query=${searchText}`, { method: "GET" }),
          ]);

        if (
          courses_response.ok &&
          articles_response.ok &&
          quicklinks_response.ok
        ) {
          const courses_data = (await courses_response.json()) as CourseProps[];
          const articles_data =
            (await articles_response.json()) as ArticleProps[];
          const quicklinks_data =
            (await quicklinks_response.json()) as QuickLinksProps[];

          setCourses(Array.isArray(courses_data) ? courses_data : []);
          setArticles(Array.isArray(articles_data) ? articles_data : []);
          setQuickLinks(Array.isArray(quicklinks_data) ? quicklinks_data : []);
        } else {
          setIsError(true);
        }
      } catch (error) {
        console.error(error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void filterSearch();
  }, [searchText]);

  useEffect(() => {
    const handleEnterKey = (event: any) => {
      if (event.key === "Enter") {
        event.preventDefault();
        searchQuery();
      }
    };
    document.addEventListener("keydown", handleEnterKey);

    return () => document.removeEventListener("keydown", handleEnterKey);
  }, [debounce, searchText, searchQuery]);

  const foreignStudentsURL = "https://www.theoicnm.com/foreign-students";
  const scholarshipURL = "https://www.theoicnm.com/scholarship";
  const admissionURL = "https://www.theoicnm.com/admission";
  const programmesURL = "https://www.theoicnm.com/programmes";
  const articlesURL = "https://www.theoicnm.com/articles";

  return (
    <div className="modal-body">
      <form role="search" onSubmit={(e) => e.preventDefault()}>
        <div className="form-block">
          <label htmlFor="site-search-input" className="sr-only">
            Search courses, articles and links
          </label>
          <div className="input-btn">
            <input
              id="site-search-input"
              name="search"
              type="search"
              placeholder="Search courses, articles and links"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <div className="search-icon" aria-hidden="true">
              <LiaSearchSolid size={25} />
            </div>
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            {isLoading
              ? "Searching…"
              : isError
                ? "Something went wrong with the search."
                : searchText
                  ? `${courses.length + articles.length + quicklinks.length} results found.`
                  : ""}
          </p>
          <div className="filter_container">
            <div className="filter_inner">
              <div>
                {courses.length > 0 && <h4>Courses</h4>}
                {courses.map((course) => (
                  <Link
                    onClick={() => setOpenModal(false)}
                    key={course?.id}
                    href={`/programmes/${course.category}/${course.slug}`}
                  >
                    <p>{course?.title}</p>
                  </Link>
                ))}
              </div>
              <div>
                {articles.length > 0 && <h4>Articles</h4>}
                {articles.map((article) => (
                  <Link
                    onClick={() => setOpenModal(false)}
                    key={article?.id}
                    href={`/articles/${article.slug}`}
                  >
                    <p>{article?.title}</p>
                  </Link>
                ))}
              </div>
              <div>
                {quicklinks.length > 0 && <h4>Quick Links</h4>}
                {quicklinks.map((link) => (
                  <Link
                    onClick={() => setOpenModal(false)}
                    key={link?.id}
                    href={`${link.url}`}
                  >
                    <p>{link?.title}</p>
                  </Link>
                ))}
              </div>
              {articles.length === 0 &&
                courses.length === 0 &&
                quicklinks.length === 0 && (
                  <div className="popular">
                    <h4>Popular Searches</h4>
                    <Link
                      onClick={() => setOpenModal(false)}
                      href={`${foreignStudentsURL}`}
                    >
                      <p>Foreign Students</p>
                    </Link>
                    <Link
                      onClick={() => setOpenModal(false)}
                      href={`${scholarshipURL}`}
                    >
                      <p>Scholarships and Financial Aids</p>
                    </Link>
                    <Link
                      onClick={() => setOpenModal(false)}
                      href={`${admissionURL}`}
                    >
                      <p>Application Procedure</p>
                    </Link>
                    <Link
                      onClick={() => setOpenModal(false)}
                      href={`${programmesURL}`}
                    >
                      <p>Programs</p>
                    </Link>
                    <Link
                      onClick={() => setOpenModal(false)}
                      href={`${articlesURL}`}
                    >
                      <p>OICNM Blog</p>
                    </Link>
                  </div>
                )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Modal;
